import { FC, useCallback, useContext, useEffect, useState } from "react";

import { Add, ChevronRight, FolderOpen } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConnectOccurrenceSummary,
  OccurrenceNumberFormat,
  getConnectOccurrenceLookup,
  getOrgConfigPortal,
  listConnectOccurrence,
  postConnectOccurrence,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

const formatWhen = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : null;

const OccurrencesPage: FC = () => {
  const { t } = useTranslation("connect");
  const navigate = useNavigate();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgId = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";
  const canCreate = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  ).canCreateConnectMeeting;

  const [occurrences, setOccurrences] = useState<ConnectOccurrenceSummary[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [referenceTag, setReferenceTag] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Per-agency config: occurrence-number format + reference-tag options.
  const [numberFormat, setNumberFormat] =
    useState<OccurrenceNumberFormat | null>(null);
  const [tagOptions, setTagOptions] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await listConnectOccurrence({ path: { org_id: orgId } });
      setOccurrences(res.data?.occurrences ?? []);
    } catch {
      toast.error(t("occurrences.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const res = await getOrgConfigPortal({ path: { org_id: orgId } });
        const cc = res.data?.connect_configuration;
        setNumberFormat(cc?.occurrence_number_format ?? null);
        setTagOptions(cc?.reference_tag_options ?? []);
      } catch {
        /* connect config is optional — fall back to free-text entry */
      }
    })();
  }, [orgId]);

  const numberMatchesFormat = (value: string) => {
    if (!numberFormat?.pattern) return true;
    try {
      return new RegExp(`^(?:${numberFormat.pattern})$`).test(value);
    } catch {
      // A misconfigured pattern shouldn't block officers (backend logs it).
      return true;
    }
  };
  const numberInvalid =
    number.trim().length > 0 && !numberMatchesFormat(number.trim());

  const openOccurrence = (id: string) =>
    navigate(`/rubiconnect/${orgId}/occurrences/${id}`);

  const handleCreate = async () => {
    const occurrenceNumber = number.trim();
    if (!occurrenceNumber) {
      toast.error(t("occurrences.toast.numberRequired"));
      return;
    }
    if (!numberMatchesFormat(occurrenceNumber)) {
      toast.error(t("occurrences.toast.numberFormatMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      // Look up first so a duplicate number opens the existing occurrence
      // instead of erroring.
      const existing = await getConnectOccurrenceLookup({
        path: { org_id: orgId },
        query: { occurrence_number: occurrenceNumber },
      });
      if (existing.data?.occurrence) {
        toast.info(t("occurrences.toast.alreadyExists"));
        openOccurrence(existing.data.occurrence.id);
        return;
      }
      const res = await postConnectOccurrence({
        path: { org_id: orgId },
        body: {
          occurrence_number: occurrenceNumber,
          reference_tag: referenceTag.trim() || undefined,
        },
      });
      toast.success(t("occurrences.toast.created"));
      const created = res.data?.occurrence;
      if (created) openOccurrence(created.id);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.console_msg ?? t("occurrences.toast.createFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const closeAdd = () => {
    if (submitting) return;
    setAddOpen(false);
    setNumber("");
    setReferenceTag("");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5">RubiCONNECT</Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddOpen(true)}
          >
            {t("occurrences.add")}
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : occurrences.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", py: 5 }}>
            <FolderOpen sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography color="text.secondary">
              {t("occurrences.empty")}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {occurrences.map((o) => {
            const last = formatWhen(o.last_meeting_at);
            const next = formatWhen(o.next_meeting_at);
            return (
              <Card key={o.id} variant="outlined">
                <CardActionArea onClick={() => openOccurrence(o.id)}>
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant="subtitle1">
                        {o.occurrence_number}
                      </Typography>
                      {o.reference_tag && (
                        <Typography variant="body2" color="text.secondary">
                          {o.reference_tag}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {next
                          ? t("occurrences.card.next", { when: next })
                          : last
                            ? t("occurrences.card.last", { when: last })
                            : t("occurrences.card.noMeetings")}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={t("occurrences.card.meetingCount", {
                        count: o.meeting_count,
                      })}
                    />
                    <ChevronRight color="action" />
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>{t("occurrences.addDialog.title")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t("occurrences.addDialog.numberLabel")}
              required
              fullWidth
              autoFocus
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={numberFormat?.example ?? undefined}
              error={numberInvalid}
              helperText={
                numberInvalid
                  ? numberFormat?.example
                    ? t("occurrences.addDialog.numberMismatchWithExample", {
                        example: numberFormat.example,
                      })
                    : t("occurrences.addDialog.numberMismatch")
                  : (numberFormat?.description ??
                    (numberFormat?.example
                      ? t("occurrences.addDialog.numberExample", {
                          example: numberFormat.example,
                        })
                      : t("occurrences.addDialog.numberHelp")))
              }
            />
            {tagOptions.length > 0 ? (
              <Autocomplete
                freeSolo
                options={tagOptions}
                inputValue={referenceTag}
                onInputChange={(_, v) => setReferenceTag(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("occurrences.addDialog.referenceTagLabel")}
                    helperText={t("occurrences.addDialog.referenceTagPickHelp")}
                  />
                )}
              />
            ) : (
              <TextField
                label={t("occurrences.addDialog.referenceTagLabel")}
                fullWidth
                value={referenceTag}
                onChange={(e) => setReferenceTag(e.target.value)}
                helperText={t("occurrences.addDialog.referenceTagHelp")}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} disabled={submitting}>
            {t("occurrences.addDialog.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting || !number.trim() || numberInvalid}
          >
            {submitting
              ? t("occurrences.addDialog.working")
              : t("occurrences.addDialog.continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OccurrencesPage;
